import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { theme } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import { AdSlot } from "scmp-app/components/advertisement/ad-slots/ad-slot";
import type { YoutubeVolumeData } from "scmp-app/components/scmp-youtube-video/scmp-youtube-video-player/types";
import type { VideoPlayerYouTubeEventCustomizedParameters } from "scmp-app/components/tracking/google-analytics-4/types";
import { useTrackCurrentItem } from "scmp-app/lib/current-item";
import type { latestVideoQuery$key } from "scmp-app/queries/__generated__/latestVideoQuery.graphql";

import {
  AdContainer,
  Container,
  OuterContainer,
  StyledVideoListGridCard,
  StyledVideoPrimary,
  StyledVideoUpNextWidget,
  TopPicksContainer,
} from "./styles";

type Props = {
  adZone?: string;
  className?: string;
  ga4Position?: VideoPlayerYouTubeEventCustomizedParameters["position"];
  reference: latestVideoQuery$key;
  showTopPicks?: boolean;
};

export const VideoLatestVideo: FunctionComponent<Props> = ({
  adZone = "default",
  className,
  ga4Position,
  reference,
  showTopPicks = false,
}) => {
  const { trackCurrentItem } = useTrackCurrentItem();

  const data = useFragment(
    graphql`
      fragment latestVideoQuery on Query
      @argumentDefinitions(videoLatestQueueName: { type: "String" }) {
        videoTop: queue(filter: { name: $videoLatestQueueName }) {
          items(first: 29) {
            edges {
              node {
                __typename
                ... on Video {
                  entityId
                  latestVideoYoutubeId: youtubeId
                  videoSource {
                    id
                    type
                  }
                  ...videoListGridCardContent
                  ...videoPrimaryContent
                }
                ...hooksTrackCurrentItemBase
              }
              ...videoUpNextWidgetVideo
            }
          }
        }
      }
    `,
    reference,
  );

  const videos = useMemo(
    () =>
      (data.videoTop?.items?.edges ?? []).filter(
        ({ node }) =>
          node.__typename === "Video" &&
          (notEmpty(node.latestVideoYoutubeId) ||
            node.videoSource?.some(source => source?.type === "youtube" && notEmpty(source.id))),
      ),
    [data.videoTop?.items?.edges],
  );

  const upNextVideos = useMemo(() => videos.slice(0, 20), [videos]);
  const topPicksVideos = useMemo(() => videos.slice(20), [videos]);

  const [currentVideoId, setCurrentVideoId] = useState(first(upNextVideos)?.node.entityId);
  const [autoplayEnabled, setAutoplayEnabled] = useState(true);
  const [isMuted, setIsMuted] = useState(true);

  const currentVideo = useMemo(
    () => upNextVideos.find(({ node }) => node.entityId === currentVideoId),
    [currentVideoId, upNextVideos],
  );

  useEffect(() => {
    if (!notEmpty(currentVideo?.node)) return;
    trackCurrentItem(currentVideo.node);
    return () => trackCurrentItem();
  }, [currentVideo?.node, trackCurrentItem]);

  const playNext = useCallback(() => {
    setCurrentVideoId(current => {
      const currentIndex = upNextVideos.findIndex(({ node }) => node.entityId === current);
      const nextIndex = currentIndex + 1;
      if (nextIndex >= upNextVideos.length) return current;
      return upNextVideos[nextIndex].node.entityId;
    });
  }, [upNextVideos]);

  const handleEnded = useCallback(() => {
    if (!autoplayEnabled) return;
    if (
      upNextVideos.findIndex(({ node }) => node.entityId === currentVideoId) + 1 >=
      upNextVideos.length
    )
      return;
    playNext();
  }, [autoplayEnabled, currentVideoId, playNext, upNextVideos]);

  const handleSelectVideo = useCallback((id: string) => {
    setCurrentVideoId(id);
    setIsMuted(false);
  }, []);

  const handleToggleAutoplay = useCallback((enabled: boolean) => {
    setAutoplayEnabled(enabled);
  }, []);

  const handleVolumeChanged = useCallback((data: YoutubeVolumeData) => {
    setIsMuted(current => {
      if (data.info?.muted === undefined) return current;
      return data.info.muted;
    });
  }, []);

  return (
    <OuterContainer className={className}>
      <Container>
        <StyledVideoPrimary
          enableAutoPlay={autoplayEnabled}
          ga4Position={ga4Position}
          isMuted={isMuted}
          onEnded={handleEnded}
          onVolumeChanged={handleVolumeChanged}
          reference={currentVideo?.node}
        />

        <AdContainer>
          <AdSlot
            adUnit="d_lrec1"
            breakpoint={theme.breakpoints.up("desktop")}
            sizes={[[300, 250]]}
            stickyLabel={true}
            withBackground={true}
            withLabel={true}
            withSticky={true}
            zone={adZone}
          />
          <AdSlot
            adUnit="m_lrec1"
            breakpoint={theme.breakpoints.down("desktop")}
            sizes={[[300, 250]]}
            withBackground={true}
            withLabel={true}
            zone={adZone}
          />
        </AdContainer>

        {notEmpty(upNextVideos) && (
          <StyledVideoUpNextWidget
            autoplayEnabled={autoplayEnabled}
            currentVideoId={currentVideoId}
            onSelect={handleSelectVideo}
            onToggleAutoplay={handleToggleAutoplay}
            reference={upNextVideos}
          />
        )}
      </Container>

      {showTopPicks && notEmpty(topPicksVideos) && (
        <TopPicksContainer>
          {topPicksVideos.map(({ node }) => (
            <StyledVideoListGridCard key={node.entityId} reference={node} />
          ))}
        </TopPicksContainer>
      )}
    </OuterContainer>
  );
};

VideoLatestVideo.displayName = "VideoLatestVideo";
